import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil, BookOpen, Search } from "lucide-react";

const TYPES = [
  { v: "buyer", l: "Buyer" },
  { v: "vendor", l: "Vendor" },
  { v: "contractor", l: "Contractor" },
  { v: "other", l: "Other" },
];
const VTYPES = [
  { v: "sales", l: "Sales (Dr)" },
  { v: "purchase", l: "Purchase (Cr)" },
  { v: "receipt", l: "Receipt (Cr)" },
  { v: "payment", l: "Payment (Dr)" },
  { v: "debit_note", l: "Debit Note (Dr)" },
  { v: "credit_note", l: "Credit Note (Cr)" },
];
const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const TYPE_L = { buyer: "Buyer", vendor: "Vendor", contractor: "Contractor", other: "Other" };
const TYPE_BADGE = {
  buyer: "border-primary/30 bg-primary/10 text-primary",
  vendor: "border-ochre/30 bg-ochre/10 text-ochre",
  contractor: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600",
  other: "border-border bg-muted text-muted-foreground",
};
const empty = { name: "", type: "buyer", gst: "", address: "", contact: "", email: "", company_id: "shared" };

export default function Ledgers() {
  const { user } = useAuth();
  const canManage = ["admin", "manager", "store_keeper"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [companies, setCompanies] = useState([]);
  const [coFilter, setCoFilter] = useState("all");
  const [stmtLed, setStmtLed] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [vform, setVform] = useState({ vtype: "sales", date: "", amount: "", ref: "", note: "" });

  const load = () => api.get("/ledgers").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  const coName = (id) => (!id || id === "shared" ? "Both" : (companies.find((c) => c.id === id)?.code || "—"));

  const openStmt = async (led) => {
    setStmtLed(led); setStmt(null);
    setVform({ vtype: "sales", date: new Date().toISOString().slice(0, 10), amount: "", ref: "", note: "" });
    try { const { data } = await api.get(`/ledgers/${led.id}/statement`); setStmt(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reloadStmt = async () => { if (stmtLed) { const { data } = await api.get(`/ledgers/${stmtLed.id}/statement`); setStmt(data); } };
  const addVoucher = async () => {
    try {
      await api.post("/vouchers", { ledger_id: stmtLed.id, ledger_name: stmtLed.name, vtype: vform.vtype, date: vform.date, amount: Number(vform.amount) || 0, ref: vform.ref, note: vform.note });
      toast.success("Voucher added");
      setVform({ ...vform, amount: "", ref: "", note: "" });
      reloadStmt();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delVoucher = async (id) => { await api.delete(`/vouchers/${id}`); toast.success("Deleted"); reloadStmt(); };

  const openNew = () => { setEditing(null); setForm({ ...empty, type: tab === "all" ? "buyer" : tab }); setOpen(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ ...empty, ...b }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/ledgers/${editing}`, form);
      else await api.post("/ledgers", form);
      toast.success("Ledger saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this ledger? This cannot be undone.")) return;
    try { await api.delete(`/ledgers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const byType = tab === "all" ? items : items.filter((i) => (i.type || "buyer") === tab);
  const byTab = coFilter === "all" ? byType : byType.filter((i) => [coFilter, "shared", "", undefined].includes(i.company_id));
  const shown = q.trim()
    ? byTab.filter((i) => [i.name, i.gst, i.contact, i.email].filter(Boolean).join(" ").toLowerCase().includes(q.trim().toLowerCase()))
    : byTab;

  return (
    <div className="space-y-8">
      <PageHeader title="Ledgers" subtitle="Master of buyers, vendors, contractors & others — linked across products, costing & orders." icon={Building2}
        action={canManage && (
          <Button data-testid="add-ledger-button" onClick={openNew} className="gap-2 transition-transform duration-200 hover:-translate-y-px">
            <Plus className="h-4 w-4" /> New Ledger
          </Button>
        )} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {[["all", "All"], ...TYPES.map((t) => [t.v, t.l + "s"])].map(([v, l]) => (
            <button key={v} data-testid={`ledger-tab-${v}`} onClick={() => setTab(v)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${tab === v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
        {companies.length > 1 && (
          <Select value={coFilter} onValueChange={setCoFilter}>
            <SelectTrigger data-testid="ledger-filter-company" className="sm:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="ledger-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search account by name, GST, contact…" className="pl-9" />
        </div>
        </div>
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={Building2} title="No ledgers yet" hint="Add a buyer, vendor or contractor to link products and orders." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Company</TableHead><TableHead>GST</TableHead>
                <TableHead>Contact</TableHead><TableHead>Address</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((b) => (
                <TableRow key={b.id} data-testid={`ledger-row-${b.id}`} className="hover:bg-muted/40">
                  <TableCell className="font-semibold">
                    <button data-testid={`ledger-name-${b.id}`} onClick={() => openStmt(b)}
                      className="text-left font-semibold text-primary underline-offset-2 transition-colors hover:underline">
                      {b.name}
                    </button>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-full text-[11px] font-semibold ${TYPE_BADGE[b.type || "buyer"]}`}>
                      {TYPE_L[b.type || "buyer"]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" data-testid={`ledger-company-${b.id}`} className="rounded-full text-[11px]">{coName(b.company_id)}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{b.gst || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{b.contact || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{b.address || "—"}</TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`ledger-statement-${b.id}`} onClick={() => openStmt(b)}
                          className="h-8 w-8 text-muted-foreground hover:text-primary" title="View ledger / vouchers"><BookOpen className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`ledger-edit-${b.id}`} onClick={() => openEdit(b)}
                          className="h-8 w-8 text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`ledger-delete-${b.id}`} onClick={() => remove(b.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} Ledger</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label>
                <Input data-testid="ledger-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Crate & Barrel (US)" /></div>
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="ledger-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>GST / Tax No</Label>
                <Input data-testid="ledger-gst" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
              <div className="space-y-2"><Label>Contact</Label>
                <Input data-testid="ledger-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            </div>
            {companies.length > 1 && (
              <div className="space-y-2"><Label>Company</Label>
                <Select value={form.company_id || "shared"} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger data-testid="ledger-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">Both companies</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <div className="space-y-2"><Label>Email</Label>
              <Input data-testid="ledger-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Address</Label>
              <Textarea data-testid="ledger-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="ledger-save" onClick={save} disabled={!form.name}>Save Ledger</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stmtLed} onOpenChange={(v) => { if (!v) { setStmtLed(null); setStmt(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2 font-heading">
            Ledger — {stmtLed?.name}
            {stmtLed && <Badge variant="outline" className={`rounded-full text-[11px] font-semibold ${TYPE_BADGE[stmtLed.type || "buyer"]}`}>{TYPE_L[stmtLed.type || "buyer"]}</Badge>}
          </DialogTitle></DialogHeader>
          {canManage && (
            <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-3 sm:grid-cols-6">
              <div className="col-span-2"><Label className="text-xs">Type</Label>
                <Select value={vform.vtype} onValueChange={(v) => setVform({ ...vform, vtype: v })}>
                  <SelectTrigger data-testid="voucher-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{VTYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Date</Label><Input type="date" data-testid="voucher-date" value={vform.date} onChange={(e) => setVform({ ...vform, date: e.target.value })} /></div>
              <div><Label className="text-xs">Amount ₹</Label><Input type="text" inputMode="decimal" data-testid="voucher-amount" value={vform.amount} onChange={(e) => setVform({ ...vform, amount: e.target.value })} /></div>
              <div><Label className="text-xs">Ref</Label><Input data-testid="voucher-ref" value={vform.ref} onChange={(e) => setVform({ ...vform, ref: e.target.value })} placeholder="Inv/PO no" /></div>
              <div className="flex items-end"><Button className="w-full gap-1" data-testid="voucher-add" onClick={addVoucher} disabled={!Number(vform.amount)}><Plus className="h-4 w-4" /> Add</Button></div>
              <div className="col-span-2 sm:col-span-6"><Input data-testid="voucher-note" value={vform.note} onChange={(e) => setVform({ ...vform, note: e.target.value })} placeholder="Note (optional)" /></div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-muted/40 p-2"><p className="text-[11px] uppercase text-muted-foreground">Total Debit</p><p className="font-heading font-bold">{money(stmt?.totals?.debit)}</p></div>
            <div className="rounded-md bg-muted/40 p-2"><p className="text-[11px] uppercase text-muted-foreground">Total Credit</p><p className="font-heading font-bold">{money(stmt?.totals?.credit)}</p></div>
            <div className="rounded-md bg-primary/10 p-2"><p className="text-[11px] uppercase text-primary">Balance</p><p className="font-heading font-bold text-primary" data-testid="ledger-balance">{(stmt?.totals?.balance || 0) === 0 ? money(0) : `${money(Math.abs(stmt.totals.balance))} ${stmt.totals.balance > 0 ? "Dr" : "Cr"}`}</p></div>
          </div>
          <div className="max-h-[45vh] overflow-y-auto">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Ref / Note</TableHead>
                <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(stmt?.entries || []).length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No vouchers yet. Add one above.</TableCell></TableRow>
                ) : stmt.entries.map((e) => (
                  <TableRow key={e.id} data-testid={`voucher-row-${e.id}`} className="hover:bg-muted/40">
                    <TableCell className="whitespace-nowrap text-xs">{e.date}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full text-[11px]">{e.vtype_label}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{[e.ref, e.note].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="text-right">{e.debit ? money(e.debit) : "—"}</TableCell>
                    <TableCell className="text-right">{e.credit ? money(e.credit) : "—"}</TableCell>
                    <TableCell className="text-right font-semibold">{money(Math.abs(e.balance))} {e.balance >= 0 ? "Dr" : "Cr"}</TableCell>
                    <TableCell className="text-right">{canManage && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" data-testid={`voucher-del-${e.id}`} onClick={() => delVoucher(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
